import {
  Flex,
  Text,
  Button,
  HStack,
  Spacer,
  IconButton,
  Center,
  Tabs,
  TabList,
  TabPanels,
  Select,
  Tab,
  TabPanel,
  VStack,
  Box,
  TabIndicator,
  Spinner,
  UnorderedList,
  ListItem,
  Input,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React from 'react';
import { SmallCloseIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import useUser from '../hooks/useUser';
import mixpanel from 'mixpanel-browser';
import { registerWithEmailAndPassword } from '../firebase';
import { useDispatch } from 'react-redux';
import { updateLabor } from '../features/labor/laborSlice';
import { updatePrice } from '../features/price/priceSlice';
import LogoutButton from './LogoutButton';
import GeneralSettings from './settings/GeneralSettings';

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const currentPath = location.pathname;
  const { user, isUserLoading } = useUser();
  const toast = useToast();
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isReviewLoading, setIsReviewLoading] = useState(false);
  const [salespersons, setSalespersons] = useState([]);
  const [salespersonEmail, setSalespersonEmail] = useState('');
  const [salespersonPassword, setSalespersonPassword] = useState('');
  const [salespersonName, setSalespersonName] = useState('');
  const [salespersonPhone, setSalespersonPhone] = useState('');
  const [salespersonIsRestricted, setSalespersonIsRestricted] = useState('');
  const [salespersonToDelete, setSalespersonToDelete] = useState(null);
  const [profileName, setProfileName] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [isProfileSaving, setIsProfileSaving] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isReviewOpen,
    onOpen: onReviewOpen,
    onClose: onReviewClose,
  } = useDisclosure();
  const cancelRef = React.useRef();
  const cancelReviewRef = React.useRef();

  const handleAddUser = async event => {
    event.preventDefault();
    setAddUserLoading(true);
    try {
      const token = await user.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/create_salesperson`,
        {
          administrator: user.email,
          username: salespersonEmail,
          password: salespersonPassword,
          isRestricted: salespersonIsRestricted,
          name: salespersonName,
          phone: salespersonPhone,
        },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      setSalespersons(prevList => [
        ...prevList,
        {
          email: salespersonEmail,
          password: salespersonPassword,
          isRestricted: salespersonIsRestricted,
          name: salespersonName,
          phone: salespersonPhone,
        },
      ]);
      setSalespersonEmail('');
      setSalespersonPassword('');
      setSalespersonName('');
      setSalespersonPhone('');
      setSalespersonIsRestricted('');
    } catch (e) {
      console.error(e);
      let tempDescription = 'Please try again later';
      if (e?.response?.data?.error === 'User already exists') {
        tempDescription = 'User with this email already exists';
      }
      toast({
        title: 'Error adding salesperson',
        description: tempDescription,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    setAddUserLoading(false);
  };

  const handleIsRestrictedChange = async (username, isRestricted) => {
    try {
      const token = await user.getIdToken();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/update_salesperson`,
        {
          username: username,
          isRestricted: isRestricted,
        },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      console.log(response);
      await fetchSalespersons();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSalespersonContactEdit = (email, field, value) => {
    setSalespersons(prevList =>
      prevList.map(sp => (sp.email === email ? { ...sp, [field]: value } : sp))
    );
  };

  const handleSalespersonContactSave = async (email, field, value) => {
    try {
      const token = await user.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/update_salesperson`,
        {
          username: email,
          [field]: value,
        },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSalespersons = async () => {
    setIsLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/salespersons`,
        {
          headers: {
            authorization: `${token}`,
          },
          params: {
            username: user.email,
          },
        }
      );
      setSalespersons(response.data);
      console.log(response);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setIsLoading(false);
  };

  const onDeleteConfirm = async () => {
    if (!salespersonToDelete) {
      onClose();
      return;
    }
    try {
      setIsDeleteLoading(true);
      const token = await user.getIdToken();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/delete_salesperson`,
        {
          username: salespersonToDelete,
        },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      console.log(response);
      setIsDeleteLoading(false);
      onClose();
      await fetchSalespersons();
    } catch (error) {
      console.error(error);
      setIsDeleteLoading(false);
    }
  };

  const wait = n => new Promise(resolve => setTimeout(resolve, n));

  const onReviewConfirm = async () => {
    try {
      setIsReviewLoading(true);
      const token = await user.getIdToken();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/review_prices`,
        {
          username: user.email,
        },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      // await wait(5000);
      setIsReviewLoading(false);
      onReviewClose();
      dispatch(updatePrice({ hasBeenSaved: [] }));
      dispatch(updateLabor({ hasBeenSaved: [] }));
      toast({
        title: 'Success',
        description: 'Please review prices',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
      setIsReviewLoading(false);
      toast({
        title: 'Error',
        description: 'Please try again later',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchProfile = async () => {
    try {
      const token = await user.getIdToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/search`,
        {
          headers: {
            authorization: `${token}`,
          },
          params: {
            username: user.email,
          },
        }
      );
      setProfileName(response.data?.name || '');
      setProfilePhone(response.data?.phone || '');
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleSaveProfile = async event => {
    event.preventDefault();
    setIsProfileSaving(true);
    try {
      const token = await user.getIdToken();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/profile`,
        { name: profileName, phone: profilePhone },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      setProfileName(response.data.name);
      setProfilePhone(response.data.phone);
      toast({
        title: 'Saved',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error',
        description: 'Your contact info could not be saved.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    setIsProfileSaving(false);
  };

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  useEffect(() => {
    if (user && user.profile === 'admin') {
      // fetch related users
      fetchSalespersons();
    }
    console.log(user);
  }, [user]);

  return (
    <Center m="20px">
      <VStack width="100%">
        <HStack width="100%" justify="center" align="center">
          {user?.profile === 'admin' && (
            <Button variant="solid" colorScheme="blue" onClick={onReviewOpen}>
              <Text fontSize="sm">Review Pricing</Text>
            </Button>
          )}
          <LogoutButton />
        </HStack>
        {isUserLoading === false && (
          <Tabs width="700px">
            <TabList>
              {user?.profile === 'admin' && (
                <>
                  <Tab>
                    <Text as="b">Company</Text>
                  </Tab>
                  <Tab>
                    <Text as="b">Estimates</Text>
                  </Tab>
                  <Tab>
                    <Text as="b">Users</Text>
                  </Tab>
                </>
              )}
              <Tab>
                <Text as="b">About</Text>
              </Tab>
            </TabList>
            <TabPanels>
              {user?.profile === 'admin' && (
                <TabPanel>
                  <GeneralSettings section="company" />
                </TabPanel>
              )}
              {user?.profile === 'admin' && (
                <TabPanel>
                  <GeneralSettings section="estimates" />
                </TabPanel>
              )}
              {user?.profile === 'admin' && (
                <TabPanel>
                  <VStack>
                    <form onSubmit={handleAddUser}>
                      <FormControl id="email" isRequired mb={3}>
                        <FormLabel fontSize="sm">Email address</FormLabel>
                        <Input
                          maxWidth="200px"
                          size="xs"
                          value={salespersonEmail}
                          onChange={e => setSalespersonEmail(e.target.value)}
                        />
                      </FormControl>
                      <FormControl id="password" isRequired mb={3}>
                        <FormLabel fontSize="sm">Password</FormLabel>
                        <Input
                          maxWidth="200px"
                          size="xs"
                          value={salespersonPassword}
                          minLength={6}
                          onChange={e => setSalespersonPassword(e.target.value)}
                        />
                      </FormControl>
                      <FormControl id="salespersonName" mb={3}>
                        <FormLabel fontSize="sm">Name</FormLabel>
                        <Input
                          maxWidth="200px"
                          size="xs"
                          value={salespersonName}
                          onChange={e => setSalespersonName(e.target.value)}
                        />
                      </FormControl>
                      <FormControl id="salespersonPhone" mb={3}>
                        <FormLabel fontSize="sm">Phone</FormLabel>
                        <Input
                          maxWidth="200px"
                          size="xs"
                          type="tel"
                          value={salespersonPhone}
                          onChange={e => setSalespersonPhone(e.target.value)}
                        />
                      </FormControl>
                      <FormControl id="isRestricted" isRequired>
                        <HStack align="center">
                          <FormLabel fontSize="sm">Is Restricted?</FormLabel>
                        </HStack>
                        <RadioGroup
                          value={salespersonIsRestricted}
                          onChange={value => {
                            setSalespersonIsRestricted(value);
                          }}
                        >
                          <HStack spacing="24px">
                            <Radio value="true">Yes</Radio>
                            <Radio value="false">No</Radio>
                          </HStack>
                        </RadioGroup>
                      </FormControl>
                      <Box textAlign="center" pb={4}>
                        <Button
                          type="submit"
                          colorScheme="blue"
                          mt={4}
                          w="100%"
                        >
                          {addUserLoading ? (
                            <Spinner />
                          ) : (
                            <Text>Add Salesperson</Text>
                          )}
                        </Button>
                      </Box>
                    </form>
                    {isLoading ? (
                      <Spinner
                        thickness="6px"
                        speed="1.25s"
                        size="lg"
                        emptyColor="gray.200"
                        color="black.500"
                      />
                    ) : (
                      <UnorderedList spacing={2} pb={3}>
                        {salespersons?.map((item, index) => (
                          <ListItem key={index}>
                            <HStack spacing="50px">
                              <HStack>
                                <Text as="b">Email: </Text>
                                <Text>{item.email}</Text>
                              </HStack>
                              <HStack>
                                <Text as="b">Password:</Text>
                                <Text> {item.password ?? '******'}</Text>
                              </HStack>
                              <HStack>
                                <Text as="b">Name:</Text>
                                <Input
                                  size="xs"
                                  width="120px"
                                  flexShrink={0}
                                  value={item.name || ''}
                                  onChange={e =>
                                    handleSalespersonContactEdit(
                                      item.email,
                                      'name',
                                      e.target.value
                                    )
                                  }
                                  onBlur={e =>
                                    handleSalespersonContactSave(
                                      item.email,
                                      'name',
                                      e.target.value
                                    )
                                  }
                                />
                              </HStack>
                              <HStack>
                                <Text as="b">Phone:</Text>
                                <Input
                                  size="xs"
                                  width="120px"
                                  flexShrink={0}
                                  type="tel"
                                  value={item.phone || ''}
                                  onChange={e =>
                                    handleSalespersonContactEdit(
                                      item.email,
                                      'phone',
                                      e.target.value
                                    )
                                  }
                                  onBlur={e =>
                                    handleSalespersonContactSave(
                                      item.email,
                                      'phone',
                                      e.target.value
                                    )
                                  }
                                />
                              </HStack>
                              <HStack>
                                <Text as="b" fontSize="sm" whiteSpace="nowrap">
                                  Is Restricted:
                                </Text>
                                {/* <Text>{item.isRestricted ?? 'No'}</Text> */}
                                <Select
                                  size="xs"
                                  minWidth="70px"
                                  value={item.isRestricted}
                                  onChange={event => {
                                    handleIsRestrictedChange(
                                      item.email,
                                      event.target.value
                                    );
                                  }}
                                >
                                  <option value={true}>Yes</option>
                                  <option value={false}>No</option>
                                </Select>
                              </HStack>
                              <SmallCloseIcon
                                onClick={() => {
                                  setSalespersonToDelete(item.email);
                                  onOpen();
                                }}
                              />
                            </HStack>
                          </ListItem>
                        ))}
                      </UnorderedList>
                    )}
                  </VStack>
                </TabPanel>
              )}
              <TabPanel>
                <VStack align="flex-start" ml="50px">
                  <HStack>
                    <Text as="b">Email:</Text>
                    <Text> {user?.email}</Text>
                  </HStack>
                  <HStack>
                    <Text as="b">Profile: </Text>
                    <Text>{user?.profile}</Text>
                  </HStack>
                  {user?.profile === 'salesperson' && (
                    <HStack>
                      <Text as="b">Administrator: </Text>
                      <Text>{user?.administrator}</Text>
                    </HStack>
                  )}
                  <Box as="form" onSubmit={handleSaveProfile} pt={6} w="320px">
                    <FormControl id="profileName" mb={3}>
                      <FormLabel fontSize="sm">Name</FormLabel>
                      <Input
                        size="sm"
                        value={profileName}
                        maxLength={120}
                        onChange={e => setProfileName(e.target.value)}
                      />
                    </FormControl>
                    <FormControl id="profilePhone" mb={3}>
                      <FormLabel fontSize="sm">Phone</FormLabel>
                      <Input
                        size="sm"
                        type="tel"
                        value={profilePhone}
                        maxLength={40}
                        onChange={e => setProfilePhone(e.target.value)}
                      />
                    </FormControl>
                    <Button
                      type="submit"
                      size="sm"
                      colorScheme="blue"
                      isLoading={isProfileSaving}
                    >
                      Save
                    </Button>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        )}
        <AlertDialog
          isOpen={isOpen}
          leastDestructiveRef={cancelRef}
          onClose={onClose}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Salesperson User
              </AlertDialogHeader>
              <AlertDialogBody>
                <VStack align="flex-start" spacing="20px">
                  <Text>Are you sure? You can't undo this action.</Text>
                </VStack>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onClose}>
                  Cancel
                </Button>
                <Button colorScheme="red" onClick={onDeleteConfirm} ml={3}>
                  {isDeleteLoading ? (
                    <Spinner
                      thickness="2px"
                      speed="1.25s"
                      size="sm"
                      color="black.500"
                    />
                  ) : (
                    <Text>Delete</Text>
                  )}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
        <AlertDialog
          isOpen={isReviewOpen}
          leastDestructiveRef={cancelReviewRef}
          onClose={onReviewClose}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Review Pricing
              </AlertDialogHeader>
              <AlertDialogBody>
                <UnorderedList spacing={3}>
                  <ListItem fontSize="sm">
                    <Text>Sets all Material and Labor tabs to yellow</Text>
                  </ListItem>
                  <ListItem fontSize="sm">
                    <Text>Does not delete any pricing data</Text>
                  </ListItem>
                </UnorderedList>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelReviewRef} onClick={onReviewClose}>
                  Cancel
                </Button>
                <Button colorScheme="blue" onClick={onReviewConfirm} ml={3}>
                  {isReviewLoading ? (
                    <Spinner
                      thickness="2px"
                      speed="1.25s"
                      size="sm"
                      color="black.500"
                    />
                  ) : (
                    <Text>Continue</Text>
                  )}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </VStack>
    </Center>
  );
};

export default Settings;
